import { useMemo } from "react";
import Dropdown from "../../atoms/Dropdown";
import Input from "../../atoms/Input/Input";
import { paidFreeOptions, SecondaryDrugWithStatus } from "../../data/drugOptionsV2";
import { DEFAULT_CARE_COINS } from "../../data/nivoConsts";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import useTherapyDrugs from "../../hooks/useTherapyDrugs";
import Spinner from "../../atoms/Spinner/Spinner";
import CareCoins from "../../molecules/CareCoins/CareCoins";

export interface UpcomingCycleDetailsV2Props {
  locationState: any;
  editedDetails: Record<string, any>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  setEditedDetails: (details: any) => void;
}

const UpcomingCycleDetailsV2: React.FC<UpcomingCycleDetailsV2Props> = ({
  locationState,
  editedDetails,
  handleChange,
  setEditedDetails,
}) => {
  console.log("editedDetails", editedDetails);
  // Fetch therapy drugs from API
  const { 
    primaryDrugs, 
    getCancerTypesForPrimary,
    getSecondaryDrugsForPrimary,
    isLoading: loadingDrugs, 
    error: drugsError 
  } = useTherapyDrugs();

  const isVerifiedAndApproved = useMemo(() => {
    return locationState?.visitStatus === "VA";
  }, [locationState?.visitStatus]);

  // Get the primary drug value (from editedDetails or locationState)
  const currentPrimaryDrug = editedDetails?.primary_drug || locationState?.support_program_drug || "";

  // Get Cancer Types for Selected Primary Drug from API
  const cancerTypesForPrimaryDrug = useMemo(() => {
    if (!currentPrimaryDrug) return [];
    return getCancerTypesForPrimary(currentPrimaryDrug);
  }, [currentPrimaryDrug, getCancerTypesForPrimary]);

  // Get Therapies for Selected Cancer Type
  const therapyOptions = useMemo(() => {
    const cancerTypeValue = editedDetails?.cancer_type || locationState?.visitMetaInfo?.cancer_type;
    if (!cancerTypeValue) return [];
    
    const selectedCancerType = cancerTypesForPrimaryDrug.find(
      (cancerType) => cancerType.value === cancerTypeValue
    );
    return selectedCancerType?.therapies || [];
  }, [cancerTypesForPrimaryDrug, editedDetails?.cancer_type, locationState?.visitMetaInfo?.cancer_type]);

  // Get Indications for Selected Therapy
  const indicationOptions = useMemo(() => {
    const therapyValue = editedDetails?.therapy_type || locationState?.visitMetaInfo?.therapy_type;
    if (!therapyValue) return [];
    
    const selectedTherapy = therapyOptions.find(
      (therapy) => therapy.value === therapyValue
    );
    return selectedTherapy?.indications || [];
  }, [therapyOptions, editedDetails?.therapy_type, locationState?.visitMetaInfo?.therapy_type]);

  // Get Secondary Drugs for Selected Primary Drug from API
  const secondaryDrugsForPrimary = useMemo(() => {
    if (!currentPrimaryDrug) return [];
    return getSecondaryDrugsForPrimary(currentPrimaryDrug);
  }, [currentPrimaryDrug, getSecondaryDrugsForPrimary]);

  // Handle Adding New Empty Secondary Drug Row
  const handleAddSecondaryDrugRow = () => {
    const newDrug: SecondaryDrugWithStatus = {
      drug_name: "",
      is_free: 0,
    };

    const currentDrugs = editedDetails?.secondary_drugs || [];
    setEditedDetails({
      ...editedDetails,
      secondary_drugs: [...currentDrugs, newDrug],
    });
  };

  // Handle Deleting a Secondary Drug
  const handleDeleteSecondaryDrug = (index: number) => {
    const updatedDrugs = (editedDetails?.secondary_drugs || []).filter((_: any, i: number) => i !== index);
    setEditedDetails({ ...editedDetails, secondary_drugs: updatedDrugs });
  };

  // Handle Updating Drug Name in a Row
  const handleUpdateSecondaryDrugName = (index: number, drugName: string) => {
    const updatedDrugs = (editedDetails?.secondary_drugs || []).map((drug: any, i: number) =>
      i === index ? { ...drug, drug_name: drugName } : drug
    );
    setEditedDetails({ ...editedDetails, secondary_drugs: updatedDrugs });
  };

  // Handle Updating Status of Existing Secondary Drug
  const handleUpdateSecondaryDrugStatus = (index: number, status: 0 | 1) => {
    const updatedDrugs = (editedDetails?.secondary_drugs || []).map((drug: any, i: number) =>
      i === index ? { ...drug, is_free: status } : drug
    );
    setEditedDetails({ ...editedDetails, secondary_drugs: updatedDrugs });
  };

  // Get available secondary drugs for a specific row (exclude already selected ones)
  const getAvailableSecondaryDrugs = (currentIndex: number) => {
    const selectedDrugNames = (editedDetails?.secondary_drugs || [])
      .map((d: any, idx: number) => (idx !== currentIndex ? d.drug_name : null))
      .filter((name: string) => name && name !== "");
    
    return secondaryDrugsForPrimary.filter(
      (drug) => !selectedDrugNames.includes(drug.value)
    );
  };

  // Check if there's an incomplete row (empty drug_name or not yet saved)
  const hasIncompleteRow = useMemo(() => {
    if (!editedDetails?.secondary_drugs || editedDetails.secondary_drugs.length === 0) {
      return false;
    }
    // Check if any row has empty drug_name
    return editedDetails.secondary_drugs.some((drug: any) => !drug.drug_name || drug.drug_name === "");
  }, [editedDetails?.secondary_drugs]);

  // Check if all available drugs are already selected
  const allDrugsSelected = useMemo(() => {
    if (loadingDrugs || secondaryDrugsForPrimary.length === 0) {
      return true; // Disable if loading or no drugs available
    }
    
    const selectedDrugNames = (editedDetails?.secondary_drugs || [])
      .map((d: any) => d.drug_name)
      .filter((name: string) => name && name !== "");
    
    // All drugs are selected if selected count equals available count
    return selectedDrugNames.length >= secondaryDrugsForPrimary.length;
  }, [editedDetails?.secondary_drugs, secondaryDrugsForPrimary, loadingDrugs]);

  // Disable plus button if there's an incomplete row or all drugs are selected or loading
  const isPlusButtonDisabled = hasIncompleteRow || allDrugsSelected || loadingDrugs;

  // Handle Primary Drug Change - Reset dependent fields
  const handlePrimaryDrugChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    // Get cancer types for the new primary drug
    const newCancerTypes = getCancerTypesForPrimary(value);
    
    // If there's only one cancer type, auto-select it
    let autoCancerType = "";
    let autoTherapyType = "";
    let autoIndication = "";
    
    if (newCancerTypes.length === 1) {
      autoCancerType = newCancerTypes[0].value;
      
      // Check if this cancer type has only one therapy
      const therapies = newCancerTypes[0].therapies || [];
      if (therapies.length === 1) {
        autoTherapyType = therapies[0].value;
        
        // Check if this therapy has only one indication
        const indications = therapies[0].indications || [];
        if (indications.length === 1) {
          autoIndication = indications[0].value;
        }
      }
    }
    
    setEditedDetails({
      ...editedDetails,
      primary_drug: value,
      cancer_type: autoCancerType,
      therapy_type: autoTherapyType,
      therapy_indication: autoIndication,
      secondary_drugs: [], // Clear secondary drugs when primary drug changes
    });
  };

  // Handle Cancer Type Change - Reset therapy and indication
  const handleCancerTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    // Find the selected cancer type in the current primary drug's cancer types
    const selectedCancerType = cancerTypesForPrimaryDrug.find(
      (ct) => ct.value === value
    );
    
    // If there's only one therapy, auto-select it
    let autoTherapyType = "";
    let autoIndication = "";
    
    if (selectedCancerType) {
      const therapies = selectedCancerType.therapies || [];
      if (therapies.length === 1) {
        autoTherapyType = therapies[0].value;
        
        // Check if this therapy has only one indication
        const indications = therapies[0].indications || [];
        if (indications.length === 1) {
          autoIndication = indications[0].value;
        }
      }
    }
    
    setEditedDetails({
      ...editedDetails,
      cancer_type: value,
      therapy_type: autoTherapyType,
      therapy_indication: autoIndication,
    });
  };

  // Handle Therapy Change - Reset indication
  const handleTherapyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    // Find the selected therapy in the current therapy options
    const selectedTherapy = therapyOptions.find(
      (therapy) => therapy.value === value
    );
    
    // If there's only one indication, auto-select it
    let autoIndication = "";
    
    if (selectedTherapy) {
      const indications = selectedTherapy.indications || [];
      if (indications.length === 1) {
        autoIndication = indications[0].value;
      }
    }
    
    setEditedDetails({
      ...editedDetails,
      therapy_type: value,
      therapy_indication: autoIndication,
    });
  };

  // Handle Care Coins Yes/No
  const handleRewards = (assign: boolean) => {
    const updatedDetails = { 
      ...editedDetails, 
      care_coins: assign ? DEFAULT_CARE_COINS : 0,
      isRewardsAssign: assign 
    };
    setEditedDetails(updatedDetails);
  };

  // Get primary drug from location state
  const primaryDrug = locationState?.support_program_drug || "";

  // Show loading state while fetching drugs
  if (loadingDrugs) {
    return (
      <div className="w-full flex justify-center items-center py-8">
        <Spinner />
      </div>
    );
  }

  // Show error state if drug fetching failed
  if (drugsError) {
    return (
      <div className="w-full py-4">
        <p className="text-red-500">Error loading drug options: {drugsError}</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-6 w-full mt-8">
        <div>
          <Input
            wrapperClassName="w-full"
            placeholder="Upcoming Cycle Number"
            label="Upcoming Cycle Number"
            name="cycle_number"
            value={Number(locationState?.cycleNumber) + 1 || ""}
            disabled
            type="text"
            id="cycle_number"
          />
        </div>

      {isVerifiedAndApproved ? (
        // ReadOnly Fields - Show existing data
        <>
          <div className="w-full">
            <Input
              wrapperClassName="w-full"
              placeholder="Cycle/Session Date"
              label="Cycle/Session Date"
              type="date"
              disabled
              name="scheduled_date"
              id="scheduledDate"
              value={editedDetails?.scheduled_date || ""}
            />
          </div>
          <div className="w-full">
            <Input
              wrapperClassName="w-full"
              label="Primary Drug"
              placeholder="Primary Drug"
              name="primary_drug"
              id="primaryDrug"
              value={primaryDrug}
              disabled
              type="text"
            />
          </div>
          <div className="w-full">
            <Dropdown
              options={paidFreeOptions}
              onChange={() => {}}
              value={
                locationState?.visitMetaInfo?.is_primary_drug_free !== undefined
                  ? String(locationState?.visitMetaInfo?.is_primary_drug_free)
                  : "0"
              }
              label="Primary Drug Status"
              name="is_primary_drug_free"
              disabled
            />
          </div>
        </>
      ) : (
        // Editable Fields for Upcoming Cycle
        <>
          {/* Upcoming Cycle Date */}
          <div className="w-full">
            <Input
              wrapperClassName="w-full"
              placeholder="Upcoming Cycle/Session Date"
              label="Upcoming Cycle/Session Date"
              type="date"
              name="next_cycle_date"
              id="nextCycleDate"
              value={editedDetails?.next_cycle_date || ""}
              onChange={handleChange}
              min={new Date().toISOString().split("T")[0]}
            />
          </div>

          {/* Primary Drug Selection */}
          <div className="w-full">
            <Dropdown
              options={primaryDrugs}
              onChange={handlePrimaryDrugChange}
              value={editedDetails?.primary_drug || primaryDrug || ""}
              label="Primary Drug"
              name="primary_drug"
              id="primaryDrug"
            />
          </div>

          {/* Primary Drug Status - Paid/Free */}
          <div className="w-full">
            <Dropdown
              options={paidFreeOptions}
              onChange={handleChange}
              value={
                editedDetails?.is_primary_drug_free !== undefined
                  ? String(editedDetails?.is_primary_drug_free)
                  : "0"
              }
              label="Primary Drug Status"
              name="is_primary_drug_free"
              id="isPrimaryDrugFree"
            />
          </div>

          {/* Cancer Type Selection - from API */}
          {cancerTypesForPrimaryDrug.length > 0 && (
            <div className="w-full">
              <Dropdown
                options={cancerTypesForPrimaryDrug}
                onChange={handleCancerTypeChange}
                value={editedDetails?.cancer_type || locationState?.visitMetaInfo?.cancer_type || ""}
                label="Cancer Type"
                name="cancer_type"
                id="cancerType"
              />
            </div>
          )}

          {/* Therapy/Approval Selection - from API */}
          {therapyOptions.length > 0 && (
            <div className="w-full">
              <Dropdown
                options={therapyOptions}
                onChange={handleTherapyChange}
                value={editedDetails?.therapy_type || locationState?.visitMetaInfo?.therapy_type || ""}
                label="Select Approval/Therapy"
                name="therapy_type"
                id="therapyType"
              />
            </div>
          )}

          {/* Indication Selection - from API */}
          {indicationOptions.length > 0 && (
            <div className="w-full">
              <Dropdown
                options={indicationOptions}
                onChange={handleChange}
                value={editedDetails?.therapy_indication || locationState?.visitMetaInfo?.therapy_indication || ""}
                label="Select Indication"
                name="therapy_indication"
                id="therapyIndication"
              />
            </div>
          )}

        </>
      )}
      </div>

      {/* Secondary Drug Selection Section - Full Width Below Grid */}
      {!isVerifiedAndApproved && (
        <div className="w-full mt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-bold mt-0 mb-0">Select Secondary Drugs (Optional)</p>
            
            {/* Add Button - Plus Icon */}
            <button
              onClick={handleAddSecondaryDrugRow}
              disabled={isPlusButtonDisabled}
              className={`flex items-center justify-center px-3 py-2 rounded-md ${
                isPlusButtonDisabled
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              style={{ height: "40px", minWidth: "40px" }}
              title={
                hasIncompleteRow
                  ? "Please complete the current row first"
                  : allDrugsSelected
                  ? "All available drugs are already selected"
                  : "Add Secondary Drug"
              }
            >
              <PlusOutlined style={{ fontSize: "18px" }} />
            </button>
          </div>

          {/* List of Secondary Drug Rows */}
          {editedDetails?.secondary_drugs && editedDetails?.secondary_drugs.length > 0 && (
            <div className="mt-3 space-y-4">
              {editedDetails?.secondary_drugs.map((drug: any, index: number) => (
                <div
                  key={index}
                  className="flex items-end gap-4 w-full"
                >
                  {/* Drug Dropdown */}
                  <div className="flex-1" style={{ marginRight: "8px" }}>
                    <Dropdown
                      label="Secondary Drug"
                      placeholder="Select a drug"
                      value={drug.drug_name || ""}
                      onChange={(e: any) =>
                        handleUpdateSecondaryDrugName(index, e.target.value)
                      }
                      options={getAvailableSecondaryDrugs(index).map(
                        (drugOption) => ({
                          label: drugOption.label,
                          value: drugOption.value,
                        }),
                      )}
                    />
                  </div>

                  {/* Paid/Free Status Dropdown */}
                  <div className="flex-1" style={{ marginRight: "12px" }}>
                    <Dropdown
                      options={paidFreeOptions}
                      onChange={(e: any) =>
                        handleUpdateSecondaryDrugStatus(index, parseInt(e.target.value) as 0 | 1)
                      }
                      value={String(drug.is_free !== undefined ? drug.is_free : 0)}
                      label="Drug Status"
                      placeholder="Select Status"
                    />
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeleteSecondaryDrug(index)}
                    className="flex items-center justify-center px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    style={{ height: "40px", minWidth: "40px" }}
                    title="Remove Drug"
                  >
                    <DeleteOutlined style={{ fontSize: "18px" }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Care Coins - Below all fields */}
      {locationState?.visitStatus !== "VA" && (
        <div className="w-full mt-4">
          <CareCoins
            isRewardsAssign={editedDetails?.isRewardsAssign}
            addedCareCoins={editedDetails?.care_coins || 0}
            disabled={locationState?.visitStatus === "VA"}
            handleAddCareCoins={() => handleRewards(true)}
            handleRemoveCareCoins={() => handleRewards(false)}
          />
        </div>
      )}
    </>
  );
};

export default UpcomingCycleDetailsV2;
